import {
  message,
  Typography,
} from 'antd';
import heic2any from 'heic2any';
import linkifyHtml from 'linkify-html';
import moment from 'moment';
import pluralize from 'pluralize';
import Values from 'values.js';

import API from '../api';
import { setToken } from '../context/appReducer';
import { store } from '../context/store';
import {
  DRMTypes,
  FEATURE_TYPE,
  THEME,
  ThemeType,
  TM_FEATURES,
} from '../types/baseTypes';
import {
  ICommunityMessageRoom,
  IMessageRoomParticipant,
} from '../types/communityMessagesTypes';
import { IWebinar } from '../types/feedTypes';
import { TitleConfig } from '../types/HostMetadata';
import {
  ROUTES,
  UAM_PATH_ACCESS_MAP,
} from '../types/routes';
import {
  Onboarding,
  OnboardingStatus,
  UAMPermissions,
} from '../types/userTypes';
import {
  DATE_FORMATS,
  WEBVEDA_HOSTS,
} from './constants';
import {
  DELETED_TEXT,
  DELETED_USER,
  PLAYBACK_SPEED_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
} from './data';
import { CustomNavigateFunction } from './hooks';
import { ICOLORS } from './Styles';

// plural rule for mango => mangoes
pluralize.addPluralRule(/mango$/i, 'mangoes');

export const setLocalData = (token: string | null, callback?: () => void) => {
  localStorage.removeItem(PLAYBACK_SPEED_KEY);

  if (token) localStorage.setItem(TOKEN_KEY, token);
  else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
  API.setAuthToken(token);
  store.dispatch(setToken(token));
  if (callback) setTimeout(() => callback(), 300);
};
export const getLocalData = () => localStorage.getItem(TOKEN_KEY);

// export const emailRegex =
//   /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// export const emailRegex =
//   /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// export const emailRegex =
//   /^(?!.*[\p{So}\p{Cn}])(?=[a-zA-Z0-9])[a-zA-Z0-9._+-]{1,64}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(#\d{10})?$/u;
export const emailRegex =
  /^(?!.*[\p{So}\p{Cn}])[a-zA-Z0-9](?:[a-zA-Z0-9._+-]{0,62}[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+(#\d{10})?$/u;

export const rgxNums = /^[0-9]+$/;
export const rgx10digit = /^[0-9]{10}$/;
export const rgx15digit = /^[0-9]{1,15}$/;
export const passwordRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;

export const handleHttpError = (error: any) => {
  const { response } = error;
  if (response && response.data.message) {
    message.error(response.data.message);
  } else if (error.message && typeof error.message === 'string') {
    message.error(error.message);
  } else {
    message.error('Something went wrong');
  }
  return Promise.reject(error);
};

export const navigateToOnboardingScreen = async (
  onBoardingStatus: OnboardingStatus | null,
  navigate: CustomNavigateFunction,
  reset: boolean = false,
  userAccessPermissions?: UAMPermissions[],
) => {
  const {
    customRouter: { homeRoutes, initialRoutes, menuRoutes },
    mDeeplinkUrl,
  } = store.getState().app;

  const defaultRoute = initialRoutes?.default;
  let route = defaultRoute;

  // initial redirection for UAM
  // Steps:
  // check if feed is present in homeRoutes, if yes, then feed is initial route.
  // check if default route present, if yes, then check access to default route, if yes, then default route is initial route.
  // check access to any other route in homeRoutes, if yes, then that route is initial route.
  // no access to any of the above, then access denied route is the initial route
  if (onBoardingStatus === 'creator_restricted') {
    let routeToOpen;

    // check if Feed is visible
    const isFeedVisible =
      homeRoutes?.some((r) => r.name === ROUTES.FEED) &&
      userAccessPermissions?.includes(UAMPermissions.FEED_READ);

    if (isFeedVisible) {
      routeToOpen = ROUTES.FEED;
    }

    // check if UAM has access to default route
    const defaultRouteAccess =
      UAM_PATH_ACCESS_MAP?.[
        defaultRoute?.route as keyof typeof UAM_PATH_ACCESS_MAP
      ];

    if (
      defaultRouteAccess &&
      userAccessPermissions?.includes(defaultRouteAccess)
    ) {
      routeToOpen = defaultRoute?.route as ROUTES;
    } else if (
      userAccessPermissions?.includes(UAMPermissions.FEED_READ) &&
      defaultRouteAccess === UAMPermissions.COMMUNITY_ACCESS
    ) {
      routeToOpen = ROUTES.COMMUNITY;
    } else {
      // check if UAM has access to any visible route
      const accessibleRoute = [...homeRoutes, ...menuRoutes]?.find((r) => {
        const routeAccess =
          UAM_PATH_ACCESS_MAP?.[
            r.config.path as keyof typeof UAM_PATH_ACCESS_MAP
          ];

        return routeAccess && userAccessPermissions?.includes(routeAccess);
      });

      if (accessibleRoute) {
        routeToOpen = accessibleRoute.name;
      }
    }

    navigate(routeToOpen || ROUTES.NO_ACCESS, {}, { reset });

    return;
  }

  if (onBoardingStatus) {
    route = initialRoutes?.[onBoardingStatus] || defaultRoute;
  }

  if (route && !route.isTMProject) {
    window.open(`${mDeeplinkUrl}web${route.route}`, '_self');
  } else {
    const path = route?.route || ROUTES.HOME;

    if (onBoardingStatus === 'started') {
      navigate(ROUTES.REGISTER, {}, { reset });
    } else if (onBoardingStatus) {
      navigate(path, {}, { reset });
    } else {
      const currToken = getLocalData();

      if (currToken) {
        navigate(path, {}, { reset });
      } else {
        localStorage.clear();
        navigate(ROUTES.LOGIN, {}, { reset });
      }
    }
  }
};

/** Used to extract specific keys from an object, if exclude is true, then all keys except those keys will be returned as a new object */
export const extract = function <T extends string | number | symbol = string>(
  obj: Record<T, any>,
  keys: T[],
  exclude: boolean = false,
) {
  var clone = Object.assign({}, obj);

  if (keys && keys instanceof Array) {
    for (var k in clone) {
      var hasKey = keys.indexOf(k) >= 0;
      if ((!hasKey && !exclude) || (hasKey && exclude)) {
        delete clone[k];
      }
    }
  }

  return clone;
};

export const copyToClipboard = (text: string, msg?: string) => {
  const successMsg = msg || 'Copied successfully';
  const errorMsg = 'Oops, unable to copy';

  if (navigator.clipboard) {
    navigator.clipboard.writeText(text.toString()).then(
      () => {
        message.success(successMsg);
      },
      (err) => {
        console.log(err);
        message.error(errorMsg);
      },
    );

    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful) {
      message.success(successMsg);
    } else {
      message.error(errorMsg);
    }
  } catch (err) {
    console.log(err);
    message.error(errorMsg);
  }

  document.body.removeChild(textArea);
};

export const handleShareLink = (
  pathname: string,
  shortUrl: boolean = false,
  dontCopy: boolean = false,
) => {
  const { shortUrl: shortDeeplinkUrl, deeplinkUrl } = store.getState().app;
  let updatedPathname;
  let link;
  if (shortUrl) {
    updatedPathname = pathname;
    link = `${shortDeeplinkUrl}${updatedPathname}`;
  } else {
    updatedPathname = pathname[0] === '/' ? pathname.slice(1) : pathname;
    link = `${deeplinkUrl}${updatedPathname}`;
  }
  if (dontCopy) return link;
  copyToClipboard(link);
  return link;
};

export const nFormat = (num: number, startLimit = 999, digits = 0): string => {
  if (!num) {
    return '0';
  }
  if (num <= startLimit) {
    return parseFloat(num.toFixed(digits)).toString();
  }
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((i) => num >= i.value);
  return item
    ? parseFloat((num / item.value).toFixed(digits).replace(rx, '$1')) +
        item.symbol
    : '0';
};

/* eslint-disable no-param-reassign */
/**
 * Formats a number according to specified locale and format options.
 *
 * @param {number} num - The actual number to be formatted.
 * @param {string} format - Format of the number expected with comma, default is 'NUM_INR'.
 * @param {string} digits - SHORT - 3.4M, LONG - 3,400,000 | default is 'LONG'.
 * @param {number} startLimit - Numbers less than or equal to this value won't be formatted, default is 9999.
 * @returns {string | number} Formatted number with proper symbol indicating value.
 *
 * @example
 * newNumberFormat(earnings, 'NUM_INR')
 * // returns a number with Indian styled commas
 */
export const newNumberFormat = (
  num: number,
  format: 'NUM_INR' | 'NUM_US' | 'NUM_EUR' = 'NUM_INR',
  digits: 'LONG' | 'SHORT' = 'LONG',
  startLimit: number = 9999,
): string | number => {
  if (!num) return '0';

  if (!Number.isInteger(num)) {
    num = Number(parseFloat(num.toFixed(2)));
  }

  if (num <= startLimit) return num;

  const locale: Record<string, string> = {
    NUM_INR: 'en-IN',
    NUM_US: 'en-US',
    NUM_EUR: 'en-DE',
  };

  const options: Record<string, Intl.NumberFormatOptions> = {
    LONG: {
      notation: 'standard',
      maximumFractionDigits: 2,
    },
    SHORT: {
      notation: 'compact',
      maximumFractionDigits: 2,
    },
  };

  const numSystem = new Intl.NumberFormat(locale[format], options[digits]);

  return numSystem.format(parseFloat(num.toFixed(2)));
};

export const timeAgo = (time: any) => {
  // update locale string
  moment.updateLocale('en', {
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: '%d seconds',
      ss: '%d seconds',
      m: '%d minute',
      mm: '%d minutes',
      h: '%d hour',
      hh: '%d hours',
      d: '%d day',
      dd: '%d days',
      w: '%d week',
      ww: '%d weeks',
      M: '%d month',
      MM: '%d months',
      y: '%d year',
      yy: '%d years',
    },
  });
  const timeDiff = moment(time).fromNow(true).split(' ');
  if (timeDiff[1] === 'month' || timeDiff[1] === 'months') {
    return `${parseInt(timeDiff[0], 10) * 4}w`;
  }
  return `${timeDiff[0]}${timeDiff[1].slice(0, 1)}`;
};

export const getReadableDateFormat = (date: string | Date) => {
  const dayMoment = moment(date).startOf('day');
  const dayDiff = moment().startOf('day').diff(dayMoment, 'day');
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 6) return dayMoment.format('dddd').toTitleCase();
  return dayMoment.format('DD/MM/YYYY');
};

export const durationFormat = (seconds = 0) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secondsLeft = seconds % 60;
  const hoursString = hours > 0 ? `${Math.floor(hours)}h ` : '';
  const minutesString = minutes > 0 ? `${Math.floor(minutes)}m ` : '';
  const secondsString = `${Math.floor(secondsLeft)}s`;
  return `${hoursString}${minutesString}${secondsString}`;
};

/** Returns the current browser and drmType */
export const checkBrowser = (): {
  browser: string;
  drmType: DRMTypes;
} => {
  const agent = navigator.userAgent.toLowerCase();
  let currBrowser = 'Chrome';
  let currDrmType: DRMTypes = 'Widevine';
  if (agent.indexOf('safari') > -1) {
    // Chrome or Safari
    if (agent.indexOf('opr') > -1) {
      // Opera
      currBrowser = 'Opera';
      currDrmType = 'Widevine';
    } else if (agent.indexOf('whale') > -1) {
      // Chrome
      currBrowser = 'Whale';
      currDrmType = 'Widevine';
    } else if (agent.indexOf('edg/') > -1 || agent.indexOf('Edge/') > -1) {
      // Chrome
      currBrowser = 'Edge';
      currDrmType = 'PlayReady';
    } else if (agent.indexOf('chrome') > -1) {
      // Chrome
      currBrowser = 'Chrome';
      currDrmType = 'Widevine';
    } else {
      // Safari
      currBrowser = 'Safari';
      currDrmType = 'FairPlay';
    }
  } else if (agent.indexOf('firefox') > -1) {
    // Firefox
    currBrowser = 'firefox';
    currDrmType = 'Widevine';
  }

  return { browser: currBrowser, drmType: currDrmType };
};

// make base64 image form file object
export const getBase64 = (img: Blob, callback: (arg: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () =>
    callback(reader.result?.toString() || ''),
  );
  reader.readAsDataURL(img);
};

export const convertImage = (
  type: string,
  fileData: Blob,
  callBack: (arg1: any, arg2: string) => void,
  isCrop: boolean = false,
  rowFileData?: string | ArrayBuffer | null,
) => {
  if (type === 'heic') {
    getBase64(fileData, (imageUrl) => {
      fetch(imageUrl)
        .then((res) => res.blob())
        .then((blob) => {
          return heic2any({
            blob,
            toType: 'image/jpeg',
            quality: 0.9,
          });
        })
        .then((conversionResult) => {
          const url = URL.createObjectURL(conversionResult as Blob);
          let fileImage = conversionResult;
          // @ts-ignore
          fileImage.name = `${fileData?.name.split('.')[0]}.jpeg`;
          if (isCrop) {
            callBack(url, url);
          } else callBack(fileImage, url);
        })
        .catch((e) => {
          console.log(e);
        });
    });
  } else {
    getBase64(fileData, (imageUrl) => {
      if (isCrop) {
        callBack(rowFileData, imageUrl);
      } else {
        callBack(fileData, imageUrl);
      }
    });
  }
};

export const getFileExtension = (fileName: string) => {
  const re = /(?:\.([^.]+))?$/;
  return re.exec(fileName);
};

export const createGuid = (guidLength: number) => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < guidLength; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getFileNameFromPath = (path: string) => {
  return path.replace(/^.*[\\\/]/, '');
};

type TagKeys = 'success' | 'warning' | 'danger' | 'info' | 'idle' | 'primary';

export const createTag = (
  tag: string,
  color: TagKeys,
  COLORS: ICOLORS,
  style?: React.CSSProperties,
) => {
  const tagColorMap = {
    success: COLORS.GREEN,
    info: COLORS.BLUE,
    warning: '#FFCA28',
    danger: COLORS.DANGER,
    idle: COLORS.ICON,
    primary: COLORS.PRIMARY,
  };
  return (
    <div
      key={`${tag}-${color}`}
      className="tag"
      style={{
        backgroundColor: tagColorMap[color],
        ...style,
      }}>
      <Typography.Text style={{ textTransform: 'uppercase', color: '#fff' }}>
        {tag}
      </Typography.Text>
    </div>
  );
};

declare global {
  interface String {
    /** Converts the string into title case i.e. only the first letter of each word will be capitalized.
     * @returns The title case of the string.
     * @example
     * 'hello world'.toTitleCase() // 'Hello World'
     */
    toTitleCase(): string;
    /** Converts the string into capitalized case i.e. only first letter of the string will be capitalized.
     * @returns The capitalized case of the string.
     * @example
     * 'hello world'.toCapitalCase() // 'Hello world'
     */
    toCapitalCase(): string;
    /**
     * Converts the string into a short form by taking the first character of every word.
     * @param caseType - The case type to format the result ('lower' | 'title' | 'upper'). Defaults to 'upper'.
     * @returns The short form of the string with specified case formatting.
     * @example
     * 'Hello World'.toShortForm() // 'HW'
     * 'Hello World'.toShortForm('upper') // 'HW'
     * 'Hello World Programming'.toShortForm('title') // 'Hwp'
     * 'JavaScript Object Notation'.toShortForm('lower') // 'json'
     * 'Application Programming Interface'.toShortForm('upper') // 'API'
     */
    toShortForm(caseType?: 'lower' | 'title' | 'upper'): string;
    hashCode(): number;
  }
}

String.prototype.hashCode = function () {
  let hash = 0;
  let i;
  let chr;
  if (this.length === 0) return hash;
  for (i = 0; i < this.length; i += 1) {
    chr = this.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

String.prototype.toTitleCase = function () {
  return this.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
};

String.prototype.toCapitalCase = function (): string {
  return this.charAt(0).toUpperCase() + this.slice(1).toLowerCase();
};

export const getQueryString = (data: Record<string, string>) => {
  const query = Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
  return `?${query}`;
};

export const hexToHSL = (H: string) => {
  // Convert hex to RGB first
  let r: any = 0;
  let g: any = 0;
  let b: any = 0;
  if (H.length === 4) {
    r = `0x${H[1]}${H[1]}`;
    g = `0x${H[2]}${H[2]}`;
    b = `0x${H[3]}${H[3]}`;
  } else if (H.length === 7) {
    r = `0x${H[1]}${H[2]}`;
    g = `0x${H[3]}${H[4]}`;
    b = `0x${H[5]}${H[6]}`;
  }
  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s = 0;
  let l = 0;

  if (delta === 0) h = 0;
  else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return `hsl(${h},${s}%,${95}%)`;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i))?.toFixed?.(dm))} ${sizes[i]}`;
};

/**
 * get the domain replaced based on the current domain
 * Implemented for cookie in content
 * @param rootUrl domain url
 * @param url content url
 * @returns
 */
export const getModifiedURL = (rootUrl: string, url?: string) => {
  // if (url?.includes('https://tagmango.com')) {
  //   return url?.replace(
  //     'https://tagmango.com/',
  //     `${rootUrl || 'https://tagmango.com/'}`,
  //   );
  // }
  // TODO: removed cookies for now
  return url;
};

/**
 * get modified content where links are replaced with anchor tags
 * @param originalContent content to be linkified
 * @returns linkified content
 */
export const linkifyContent = (originalContent: string) =>
  linkifyHtml(originalContent, {
    defaultProtocol: 'https',
  });

/**
 * This function is used to get the resource name from the url
 * @param str url
 * @returns name of the resource
 */
export const getResourceName = (str: string) => {
  // return url.split('/').pop();
  // name-hash
  const fileName = str.split('/').pop();
  const extension = fileName?.split('.').pop();
  // remove hash
  const name = fileName?.split('-').slice(0, -1).join('-');
  return name + '.' + extension;
};

export const checkVideoFileValidation = (fileData: File) => {
  const type = fileData.type
    ? fileData.type
    : getFileExtension(fileData.name)?.[1].toLowerCase();

  let isVideo =
    type === 'video/mp4' ||
    type === 'mp4' ||
    type === 'video/webm' ||
    type === 'webm' ||
    type === 'video/quicktime' ||
    type === 'quicktime' ||
    type === 'video/mov' ||
    type === 'mov' ||
    type === 'video/mkv' ||
    type === 'mkv';

  if (type === 'video/mp4' || type === 'mp4') {
    const extention = getFileExtension(fileData.name)?.[1].toLowerCase();
    if (extention === 'm4v') {
      isVideo = false;
    }
  }
  return isVideo;
};

// export const hashCode = (s: string) =>
//   s.split('').reduce((acc, current) => acc + current.charCodeAt(0), 0);

// /**
//  * Generate a unique id for each player instance
//  * @param mediaId media id
//  * @returns {number} player id
//  * */
// export const getPlayerId = (mediaId: string) => {
//   return hashCode(mediaId) + Math.floor(Math.random() * 100000);
// };

export const formatName = (
  name: string,
  data?: {
    nameType?: 'first' | 'last' | 'full';
    caseType?: 'upper' | 'title';
    separator?: string;
  },
): string => {
  const { nameType, caseType, separator } = data ?? {};

  const [firstName, lastName] = name.split(separator ?? ' ');

  let finalName = '';

  if (nameType) {
    switch (nameType) {
      case 'first':
        finalName = firstName;
        break;
      case 'last':
        finalName = lastName;
        break;
      case 'full':
        finalName = name;
        break;
      default:
        break;
    }
  }

  if (caseType) {
    switch (caseType) {
      case 'upper':
        return finalName.toUpperCase();
      case 'title':
        return finalName.toTitleCase();
      default:
        break;
    }
  }

  return finalName;
};

/**
 * @param color Hex value format: #ffffff
 * @param weight darken value between 0 and 100
 * @returns Hex value
 */
export const shadeColor = (color: string, weight: number): string => {
  return `#${new Values(color).shade(weight).hex}`;
};

/**
 * @param color Hex value format: #ffffff
 * @param weight lighten value between 0 and 100
 * @returns Hex value
 */
export const tintColor = (color: string, weight: number): string => {
  return `#${new Values(color).tint(weight).hex}`;
};

export const createColorShades = (
  theme: ThemeType,
  color: string,
  colorBaseName: string,
) => {
  if (theme === THEME.DARK) {
    return {
      [colorBaseName]: color,
      [`${colorBaseName}_FILL`]: shadeColor(color, 80),
      [`${colorBaseName}50`]: shadeColor(color, 80),
      [`${colorBaseName}100`]: shadeColor(color, 60),
      [`${colorBaseName}200`]: shadeColor(color, 50),
      [`${colorBaseName}300`]: shadeColor(color, 20),
      [`${colorBaseName}400`]: color,
      [`${colorBaseName}500`]: tintColor(color, 20),
      [`${colorBaseName}600`]: tintColor(color, 40),
      [`${colorBaseName}700`]: tintColor(color, 60),
      [`${colorBaseName}800`]: tintColor(color, 80),
    };
  }

  return {
    [colorBaseName]: color,
    [`${colorBaseName}_FILL`]: tintColor(color, 90),
    [`${colorBaseName}50`]: tintColor(color, 90),
    [`${colorBaseName}100`]: tintColor(color, 85),
    [`${colorBaseName}200`]: tintColor(color, 50),
    [`${colorBaseName}300`]: tintColor(color, 20),
    [`${colorBaseName}400`]: color,
    [`${colorBaseName}500`]: shadeColor(color, 20),
    [`${colorBaseName}600`]: shadeColor(color, 40),
    [`${colorBaseName}700`]: shadeColor(color, 60),
    [`${colorBaseName}800`]: shadeColor(color, 75),
  };
};

export const getMeetingLink = (videoCall: IWebinar) => {
  if (!videoCall) return;

  if (videoCall?.platform === 'custom') return videoCall?.meetingUrl;

  return `${window.location.origin}/web${ROUTES.WEBINAR_METADATA_VIEW.replace(
    ':webinarId',
    videoCall._id,
  )}`;
};

export const objToQueryParams = (data: Record<string, string>) => {
  return Object.entries(data)
    .map(([key, val]) => `${key}=${val}`)
    .join('&');
};

/**
 * to shorten the text upto specific characters limit and add ... at the end (if overflow is true)
 * @param {string} text - the text to be shorten
 * @param {number} limit - the limit upto which the text should be shorten
 * @param {boolean} overflow - if true then add ... at the end of the shorten text
 * @returns {string} - the shorten text
 * @example
 * shortenText('This is a text', 5) // This ...
 * shortenText('This is a text', 100) // This is a text
 */
export const shortenText = (
  text: string,
  limit: number,
  overflow?: boolean,
) => {
  if (text?.length > limit) {
    return `${text?.substring(0, limit)}${overflow ? '...' : ''}`;
  }

  return text;
};

/**
 * Generates a regular expression pattern for a given number of digits.
 * @param {number} digitsLength - The desired number of digits.
 * @returns {RegExp} A regular expression pattern that matches strings with the specified number of digits.
 * @throws {Error} - Throws an error if digitsLength is not a positive integer.
 */
export const generateDigitsRegex = (digitsLength: number) => {
  // Check if digitsLength is a positive integer
  if (
    typeof digitsLength !== 'number' ||
    digitsLength <= 0 ||
    !Number.isInteger(digitsLength)
  ) {
    throw new Error('Number of digits must be a positive integer');
  }

  // Create a regular expression pattern using template literals
  const regexPattern = new RegExp(`^[0-9]{${digitsLength}}$`);

  // Return the generated regular expression pattern
  return regexPattern;
};

export const detectBrowser = (ua: string) => {
  let match;

  match = ua.match(/EdgA?\/([\d.]+)/);
  if (match) return { browser: 'Edge', version: match[1] };

  match = ua.match(/OPR\/([\d.]+)|Opera\/([\d.]+)/);
  if (match) return { browser: 'Opera', version: match[1] || match[2] };

  match = ua.match(/Chrome\/([\d.]+)/);
  if (match && !/Edg|OPR/.test(ua))
    return { browser: 'Chrome', version: match[1] };

  match = ua.match(/Safari\/([\d.]+)/);
  if (match && !/Chrome/.test(ua))
    return { browser: 'Safari', version: match[1] };

  match = ua.match(/Firefox\/([\d.]+)/);
  if (match) return { browser: 'Firefox', version: match[1] };

  match = ua.match(/MSIE ([\d.]+)|Trident\/.*rv:([\d.]+)/);
  if (match)
    return { browser: 'Internet Explorer', version: match[1] || match[2] };

  return { browser: 'Unknown Browser', version: 'N/A' };
};

// const irregularPlurals = new Set([
//   'children',
//   'feet',
//   'teeth',
//   'geese',
//   'mice',
//   'men',
//   'women',
//   'oxen',
//   'lice',
//   'cacti',
//   'radii',
//   'fungi',
//   'nuclei',
//   'syllabi',
//   'alumni',
//   'data',
//   'criteria',
//   'phenomena',
// ]);

// const uncountableNouns = new Set([
//   'information',
//   'equipment',
//   'furniture',
//   'money',
//   'rice',
//   'sheep',
//   'fish',
//   'deer',
//   'species',
//   'series',
//   'news',
// ]);

/**
 * Determines if a given word is in singular or plural form.
 * @param word - The word to check.
 * @param type - Either "singular" or "plural".
 * @returns {boolean} - True if the word is in the specified form.
 */
export const checkWordForm = (
  phrase: string,
  type: 'singular' | 'plural',
): boolean => {
  if (!phrase) return false;

  // const words = phrase.toLowerCase().split(' ');
  // const lastWord = words[words.length - 1];

  // if (irregularPlurals.has(lastWord) || uncountableNouns.has(lastWord)) {
  //   return true;
  // }

  // // Common plural endings
  // const pluralPatterns = [
  //   /s$/, // Most standard plurals (cats, dogs)
  //   /xes$/, // Boxes, foxes
  //   /ches$/, // Churches, benches
  //   /shes$/, // Dishes, wishes
  //   /oes$/, // Potatoes, heroes
  //   /ies$/, // Babies, cities
  //   /ves$/, // Wolves, knives
  //   /men$/, // Women, men (irregular)
  //   /ae$/, // Antennae, formulae (Latin origin)
  //   /i$/, // Cacti, fungi
  // ];

  // const isInPluralForm = pluralPatterns.some((pattern) =>
  //   pattern.test(lastWord),
  // );

  const isInPluralForm = pluralize.isPlural(phrase);

  return type === 'plural' ? isInPluralForm : !isInPluralForm;
};

type WordForm = 'singular' | 'plural';

/**
 * Converts a word between singular and plural forms.
 * @param word - The word to convert.
 * @param targetForm - Either "singular" or "plural".
 * @returns {string} - The converted word.
 */
export const convertToSingularOrPlural = (
  word: string,
  targetForm: WordForm,
): string => {
  if (!word) return word;

  const lowerCaseWord = word.toLowerCase();

  // Check if the noun is already in the target form
  if (checkWordForm(lowerCaseWord, targetForm)) return lowerCaseWord;

  return pluralize(word, targetForm === 'plural' ? 2 : 1);

  // const pluralRules = new Intl.PluralRules('en', { type: 'cardinal' });

  // if (targetForm === 'plural') {
  //   if (/[^aeiou]y$/.test(lowerCaseWord))
  //     return `${lowerCaseWord.slice(0, -1)}ies`;
  //   if (/(s|x|z|ch|sh)$/.test(lowerCaseWord)) return `${lowerCaseWord}es`;
  //   return pluralRules.select(2) === 'one'
  //     ? lowerCaseWord
  //     : `${lowerCaseWord}s`;
  // }

  // if (targetForm === 'singular') {
  //   if (/ies$/.test(lowerCaseWord)) return `${lowerCaseWord.slice(0, -3)}y`;
  //   if (/(ses|xes|zes|ches|shes)$/.test(lowerCaseWord))
  //     return lowerCaseWord.slice(0, -2);
  //   if (lowerCaseWord.endsWith('s')) return lowerCaseWord.slice(0, -1);
  // }

  // return lowerCaseWord;
};

/**
 * pluralizeNoun - Pluralize a noun based on common rules
 * @param {string} noun - The noun to pluralize
 * @returns {string} The pluralized noun
 * @example
 * console.log(pluralizeNoun('cat')); // Output: "cats"
 * console.log(pluralizeNoun('bus')); // Output: "buses"
 * console.log(pluralizeNoun('potato')); // Output: "potatoes"
 */
export const pluralizeNoun = (noun: string): string => {
  return convertToSingularOrPlural(noun, 'plural');
};

export const getSingularOrPlural = (val: number, text: string) =>
  val > 1 ? pluralizeNoun(text) : text;

export const scrollToElement = (data: { className?: string; id?: string }) => {
  const { className, id } = data;

  if (!className && !id) return;

  let element = document.querySelector(`.${className}`);

  if (id) {
    element = document.getElementById(id);
  }

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }
};

export const getArticle = (phrase: string): string => {
  if (!phrase) return '';

  const word = phrase.trim().toLowerCase();
  const firstChar = word[0];
  const firstWord = word.split(' ')[0]; // Handle multi-word cases

  // Words that start with a vowel but use "a" (e.g., "a university", "a European")
  const aExceptions = new Set([
    'university',
    'european',
    'one',
    'unit',
    'user',
    'unique',
  ]);

  // Words that start with a consonant but use "an" (e.g., "an hour", "an honest person")
  const anExceptions = new Set(['honest', 'hour', 'heir', 'honor']);

  if (anExceptions.has(firstWord) || /^[aeiou]/.test(firstChar)) {
    return 'an';
  }

  if (aExceptions.has(firstWord) || /^[^aeiou]/.test(firstChar)) {
    return 'a';
  }

  return 'a'; // Default case
};

/**
 * Returns formatted text based on the provided text case.
 * @param word - The word to format.
 * @param textCase - The text case to format the word. (options: 'upper', 'lower', 'title', 'capital', default: 'lower')
 * @returns The formatted word based on the text case.
 * @example
 * console.log(formatCase('hello world', 'upper')); // Output: 'HELLO WORLD'
 * console.log(formatCase('hello world', 'lower')); // Output: 'hello world'
 * console.log(formatCase('hello world', 'title')); // Output: 'Hello World'
 * console.log(formatCase('hello world', 'capital')); // Output: 'Hello world'
 */
export const formatCase = (
  word: string,
  textCase: 'default' | 'upper' | 'lower' | 'title' | 'capital' = 'lower',
): string => {
  switch (textCase) {
    case 'default':
      return word;
    case 'upper':
      return word.toUpperCase();
    case 'lower':
      return word.toLowerCase();
    case 'title':
      return word.toTitleCase();
    case 'capital':
      return word.toCapitalCase();
    default:
      return word;
  }
};

type DynamicItemTitleKeyMap = {
  [K in keyof TitleConfig]: TitleConfig[K];
} & {
  'tq-title': string; // Trust Quotient (TQ)
  'tq-score': string; // TQ
  'tq-offering': string; // Service
  'tq-offerings': string; // Services
  'tq-offering-request': string; // Service Request
  'tq-offering-requests': string; // Service Requests
};

/**
 * Returns the dynamic title based on the key provided.
 * @param key - The key to get the title for items with dynamic titles. (e.g. from titleConfig, tqConnectConfig, etc.)
 * @param textCase - The text case to format the title. (options: 'upper', 'lower', 'title', 'capital', default: 'lower')
 * @param includeArticle - If true, includes the article (a/an) before the title. (default: false)
 * @returns The formatted title based on the key.
 * @example
 * console.log(dynamicItemTitle('tq-offering-request', 'lower')); // Output: 'service request'
 * console.log(dynamicItemTitle('tq-offering-request', 'upper')); // Output: 'SERVICE REQUEST'
 * console.log(dynamicItemTitle('tq-offering-request', 'title')); // Output: 'Service Request'
 * console.log(dynamicItemTitle('tq-offering-request', 'capital')); // Output: 'Service request'
 */
export const dynamicItemTitle = <Key extends keyof DynamicItemTitleKeyMap>(
  key: Key,
  textCase: 'default' | 'upper' | 'lower' | 'title' | 'capital' = 'lower',
  includeArticle: boolean = false,
): string => {
  const {
    titleConfig,
    hostMetadata: { tqConnectConfig },
  } = store.getState().app;

  const DYNAMIC_ITEM_TITLE_KEYS_MAP: DynamicItemTitleKeyMap = {
    ...titleConfig,
    'tq-title': tqConnectConfig?.label || 'Trust Quotient (TQ)',
    'tq-score': tqConnectConfig?.scoreConfig?.name || 'TQ',
    'tq-offering': tqConnectConfig?.offeringName || 'service',
    'tq-offerings': pluralizeNoun(tqConnectConfig?.offeringName || 'service'),
    'tq-offering-request': `${
      tqConnectConfig?.offeringName || 'service'
    } request`,
    'tq-offering-requests': `${
      tqConnectConfig?.offeringName || 'service'
    } requests`,
  };

  const itemTitle = DYNAMIC_ITEM_TITLE_KEYS_MAP[key];

  return `${includeArticle ? `${getArticle(itemTitle)} ` : ''}${formatCase(
    itemTitle,
    textCase,
  )}`;
};

export const reorderItemInList = (
  list: any[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const localDateTimeToISO = (dateStr: string, timeStr: string) => {
  // Combine date and time into a single string
  const dateTimeStr = `${dateStr} ${timeStr}`;

  // Parse in LOCAL time (moment defaults to local time)
  const dateTime = moment(dateTimeStr, 'DD-MM-YYYY HH:mm');

  // Format as ISO 8601 WITHOUT converting to UTC
  const localISO = dateTime.format('YYYY-MM-DDTHH:mm:ssZ');

  return localISO;
};

export const getDMUser = (
  room: ICommunityMessageRoom,
): IMessageRoomParticipant | null => {
  if (room.roomType !== 'dm') return null;

  const state = store.getState();
  const {
    hostMetadata: {
      creator: { _id: hostCreatorId },
    },
  } = state.app;
  const { id: userId, type: userType } = state.user;

  const participants = [...(room?.participants || [])];

  const otherUser = participants.filter((participant) =>
    userType === 'creator_restricted'
      ? participant._id !== hostCreatorId
      : participant._id !== userId,
  )?.[0];

  return otherUser;
};

export const isHtml = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

export const INCLUDED_SUPPORT_WIDGET_PATHS = [
  ROUTES.FEED,
  ROUTES.COMMUNITY,
  ROUTES.COURSES,
  ROUTES.STOREFRONT,
  ROUTES.POST_DETAILS,
  ROUTES.VIDEO_CALL,
];

/**
 * Shows a loading message with live progress updates using Ant Design's `message` API.
 *
 * @param title - Initial loading message
 * @returns An object with methods to update progress, mark success, or handle failure
 */
export const showLoadingToastWithProgress = (title = 'Loading...') => {
  const key = 'loading_toast_with_progress';

  // Initial loading message
  message.loading({
    type: 'loading',
    content: `${title} 0%`,
    key,
    duration: 0,
  });

  return {
    /**
     * Updates the loading message with new progress (0 to 100).
     * Automatically formats the message with a percent suffix.
     */
    update: (percent: number, customTitle?: string) => {
      message.loading({
        type: 'loading',
        content: `${customTitle || title} ${percent}%`,
        key,
        duration: 0,
      });
    },

    /**
     * Marks the operation as successful.
     */
    success: (successText = 'Completed!') => {
      message.success({
        type: 'success',
        content: successText,
        key,
        duration: 2,
      });
    },

    /**
     * Marks the operation as failed.
     */
    error: (errorText = 'Something went wrong') => {
      message.error({
        type: 'error',
        content: errorText,
        key,
        duration: 3,
      });
    },

    /**
     * Hides the message manually (if needed).
     */
    hide: () => {
      message.destroy(key);
    },
  };
};

export const checkIfUserIsHostCreator = (
  userId: string | null | undefined,
): boolean => {
  if (!userId) return false;

  const { _id: hostCreatorId } = store.getState().app.hostMetadata.creator;

  return userId === hostCreatorId;
};

export const checkIfUserIsCreator = (
  userId: string | null | undefined,
): boolean => {
  if (!userId) return false;

  const { _id: hostCreatorId } = store.getState().app.hostMetadata.creator;
  const { id: loggedInUserId, type: loggedInUserType } = store.getState().user;

  return loggedInUserType === Onboarding.CREATOR_RESTRICTED
    ? userId === hostCreatorId
    : loggedInUserId === userId;
};

/**
 * Checks if the current logged in user is the same as the given user ID. If the user is a creator restricted user, it checks if the user is the host creator.
 * @param userId - The user ID to check.
 * @returns True if the current logged in user is the same as the given user ID, false otherwise.
 */
export const checkIfCurrentLoggedInUser = (userId: string) => {
  const { user } = store.getState();

  // If the user is a creator restricted user, check if the user is the host creator
  if (user.type === 'creator_restricted') {
    return checkIfUserIsHostCreator(userId);
  }

  return user.id === userId;
};

export const getNameFromMentiontag = (tag: string) => {
  return tag.substring(tag.indexOf('#') + 1, tag.length - 2);
};

export const getIdFromMentiontag = (tag: string) => {
  return tag.substring(2, tag.indexOf('#'));
};

export const extractTextAndMentionsMap = (content: string) => {
  const regex = /{{[0-9a-f]{24}#[\s\S]*?}}/g;
  const tagsRegexResult = content.match(regex) || [];
  const splits = content.split(regex);

  const mentionsMap: Map<string, string> = new Map();
  let plainText = '';

  splits.forEach((textPart, index) => {
    plainText += textPart;

    const tag = tagsRegexResult[index];
    if (tag) {
      const userName = getNameFromMentiontag(tag);
      const userId = getIdFromMentiontag(tag);
      const mentionKey = `@${userName}`;
      const mentionValue = `{{${userId}#${userName}}}`;

      plainText += mentionKey;
      mentionsMap.set(mentionKey, mentionValue);
    }
  });

  return { text: plainText, mentionsMap };
};

// TODO: Make a flexible but advanced function to get the readable date and time format based on the options (later)
/**
 * Returns the readable date and time format for the given time.
 * @param time - The time to get the readable date and time format for.
 * @param options - Options to control display formatting.
 * - relativeTime: If true, the readable date and time format will be shown in relative format (2 days ago, 1 hour ago, etc.).
 * @returns The readable date and time format for the given time.
 * @example
 * getReadableDateAndTimeFormat('2025-10-29 12:00:00'); // '2 days ago' (if the time is less than or equal to 7 days, show the time in relative format 2 days ago, 1 hour ago, etc.)
 * getReadableDateAndTimeFormat('2025-10-20 12:00:00'); // '1 Jan' (if the time is more than 7 days, show the time in the format of D MMM (e.g. 1 Jan))
 * getReadableDateAndTimeFormat('2025-10-20 12:00:00'); // '20 Oct 2025' (if the time is greater than 7 days, show the time in the format of D MMM YYYY (e.g. 20 Oct 2025))
 */
export const getReadableDateAndTimeFormat = (
  time: string,
  options?: {
    relativeTime?: boolean;
  },
) => {
  const { relativeTime = true } = options || {};

  const daysDifference = moment().diff(moment(time), 'days');

  // if the time is less than or equal to 7 days, show the time in relative format (fromNow)
  if (daysDifference <= 7 && relativeTime) {
    return moment(time).fromNow();
  }

  // if the date is in the current year, show the date in the format of READABLE_DATE_FORMAT (e.g. Jan 1)
  if (moment(time).isSame(moment(), 'year')) {
    return moment(time).format(DATE_FORMATS.READABLE_DATE_FORMAT); // e.g. 1 Jan
  }

  return moment(time).format(DATE_FORMATS.DAY_YEAR_FORMAT); // e.g. 1 Jan 2021
};

/**
 * Returns a display-friendly user name, falling back to DELETED_USER and optionally replacing with a self-pronoun when the user matches the current user.
 * @param userId - The user ID to get the readable user name for.
 * @param userName - The user name to get the readable user name for.
 * @param options - Options to control display formatting.
 * - showReplacedByYou: If true, the readable user name will be replaced by the replacedByYouText if the current logged in user is the same as the given user ID.
 * - replacedByYouText: The text to replace the readable user name with if the current logged in user is the same as the given user ID.
 * - suffix: The suffix to add to the readable user name. Note: suffix is only appended when a user name (or DELETED_USER) is shown; it is not appended when replacedByYouText is returned.
 * @returns The readable user name for the given user ID and user name.
 * @example
 * getUserDisplayName('123', 'John Doe'); // 'John Doe' or DELETED_USER if userName is undefined or not provided
 * getUserDisplayName('123', 'John Doe', { showReplacedByYou: true, replacedByYouText: 'You' }); // 'You'
 */
export const getUserDisplayName = (
  userId?: string,
  userName?: string,
  options?: {
    showReplacedByYou?: boolean;
    replacedByYouText?: string;
    suffix?: string;
  },
): string => {
  const {
    showReplacedByYou = false,
    replacedByYouText = 'You',
    suffix = '',
  } = options || {};

  const userNameToShow = userName || DELETED_USER;

  if (
    userId &&
    showReplacedByYou &&
    replacedByYouText &&
    checkIfCurrentLoggedInUser(userId)
  ) {
    return replacedByYouText;
  }

  return `${userNameToShow}${suffix}`;
};

/**
 * Returns the display name of the item.
 * @param itemName - The name of the item.
 * @param options - Options to control display formatting.
 * - caseType: The case type to format the item name. (options: 'upper', 'lower', 'title', 'capital', default: 'lower')
 * - deletedText: The text to display if the item name is not provided. (default: CONSTANTS.COMMON.DELETED_TEXT = 'NOT_FOUND')
 * @returns The display name of the item.
 * @example
 * getItemDisplayName('John Doe'); // 'John Doe'
 * getItemDisplayName('John Doe', { caseType: 'upper' }); // 'JOHN DOE'
 */
export const getItemDisplayName = (
  itemName?: string,
  options?: {
    caseType?: 'default' | 'upper' | 'lower' | 'title' | 'capital';
    deletedText?: string;
  },
): string => {
  const { caseType = 'default', deletedText = DELETED_TEXT } = options || {};

  return itemName ? formatCase(itemName, caseType) : deletedText;
};

export const hasFeatureAccess = (feature: FEATURE_TYPE) => {
  const state = store.getState();
  const { isTagMango } = state.app;
  const { type: userType, featuresConfig } = state.user;
  const { creatorMangoes: userMangoes, subscribedMangoes } = state.user;

  const config = featuresConfig?.[feature];

  if (!config) return false;

  // check if the feature is available in which platform (in TM or WL or both)
  // mostly all features will be WL only (by default platform is unset, so it will be considered as WL feature)
  if (
    isTagMango &&
    (!config?.platform || config?.platform === 'wl') &&
    !Object.values(TM_FEATURES).includes(feature as TM_FEATURES)
  ) {
    return false;
  }

  // check if the feature has an expiry date and if it is expired
  if (config?.expiryAt && moment().isAfter(moment(config.expiryAt))) {
    return false;
  }

  // check if the feature is enabled for the user type (current logged in user)
  if (
    userType &&
    config?.allowedUsers &&
    config?.allowedUsers.length > 0 &&
    !config?.allowedUsers?.includes(userType)
  ) {
    return false;
  }

  // check if the feature is enabled for specific mangoes and if the user has any of those mangoes
  if (config?.allowedMangoes && config?.allowedMangoes?.length > 0) {
    // For UAM: check using those mangoes whose access are given to them by the creator
    // and if UAM mangoes array is empty then that UAM has access to all the mangoes
    if (userType === 'creator_restricted') {
      return (
        userMangoes.length === 0 ||
        userMangoes.some((m) => config?.allowedMangoes?.includes(m._id))
      );
    }

    // For customers: check using those mangoes they purchased or subscribed to
    if (userType === 'fan_completed' || userType === 'dual') {
      return subscribedMangoes.some((m) =>
        config?.allowedMangoes?.includes(m._id),
      );
    }

    return false;
  }

  return config?.enabled;
};

export const isWebVedaHost = (hostUrl?: string): boolean =>
  WEBVEDA_HOSTS.some((h) => hostUrl?.includes(h));

export const truncateText = (text: string, limit: number): string => {
  return text.length > limit ? `${text.slice(0, limit)}...` : text;
};
